import { useCallback, useMemo, useState } from 'react';
import type { Message } from '../types';

/** Default for `settings.behavior.maxRenderedMessages`. */
export const DEFAULT_MAX_RENDERED_MESSAGES = 80;
/** How many older messages each "Show earlier messages" click reveals. */
export const DEFAULT_EXPAND_BY = 40;

export interface UseMessageWindowOptions {
  /** Tail size; `0` renders every message. Invalid values fall back to the default. */
  maxRendered?: number;
  /** Older messages revealed per `showMore()` call. */
  expandBy?: number;
  /** Always rendered, even when it falls before the window. */
  streamingId?: string | null;
  /** Always rendered, even when they fall before the window. */
  retryingIds?: ReadonlyArray<string>;
  /**
   * Whether the log is scrolled to the bottom. A new message snaps the window
   * back to the tail only then; otherwise the first visible message stays put.
   */
  atBottom?: boolean;
}

export interface UseMessageWindowResult {
  visible: Message[];
  /** Messages before the window that are not rendered. */
  hiddenCount: number;
  showMore: () => void;
}

interface WindowState {
  /** Older messages revealed beyond `maxRendered`. */
  extra: number;
  length: number;
  firstId: string | undefined;
  lastId: string | undefined;
}

function normalizeCount(value: number | undefined, fallback: number, allowZero: boolean): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return fallback;
  const n = Math.floor(value);
  if (n === 0 && !allowZero) return fallback;
  return n;
}

function snapshot(messages: Message[], extra: number): WindowState {
  return {
    extra,
    length: messages.length,
    firstId: messages[0]?.id,
    lastId: messages[messages.length - 1]?.id,
  };
}

/**
 * Renders only the tail of a long conversation. Restored histories can hold
 * hundreds of messages; mounting them all makes opening the panel slow.
 */
export function useMessageWindow(
  messages: Message[],
  {
    maxRendered,
    expandBy,
    streamingId = null,
    retryingIds,
    atBottom = true,
  }: UseMessageWindowOptions = {}
): UseMessageWindowResult {
  const limit = normalizeCount(maxRendered, DEFAULT_MAX_RENDERED_MESSAGES, true);
  const step = normalizeCount(expandBy, DEFAULT_EXPAND_BY, false);

  const [state, setState] = useState<WindowState>(() => snapshot(messages, 0));

  // Derive the window from the message list during render (no flash of the old
  // window): appends either snap to the tail or keep the first visible message.
  let { extra } = state;
  const lastId = messages[messages.length - 1]?.id;
  if (
    messages.length !== state.length ||
    lastId !== state.lastId ||
    messages[0]?.id !== state.firstId
  ) {
    const appended =
      messages.length > state.length &&
      (state.length === 0 || messages[state.length - 1]?.id === state.lastId);
    const sameConversation = state.length > 0 && messages[0]?.id === state.firstId;
    if (atBottom) extra = 0;
    else if (appended) extra = state.extra + (messages.length - state.length);
    else if (!sameConversation) extra = 0;
    setState(snapshot(messages, extra));
  }

  const retryingKey = retryingIds ? retryingIds.join('\u0000') : '';

  const { visible, hiddenCount } = useMemo(() => {
    if (limit === 0) return { visible: messages, hiddenCount: 0 };
    const start = Math.max(0, messages.length - (limit + extra));
    if (start === 0) return { visible: messages, hiddenCount: 0 };
    const pinned = new Set<string>(retryingKey ? retryingKey.split('\u0000') : []);
    if (streamingId) pinned.add(streamingId);
    const head = pinned.size ? messages.slice(0, start).filter(m => pinned.has(m.id)) : [];
    return {
      visible: head.length ? [...head, ...messages.slice(start)] : messages.slice(start),
      hiddenCount: start - head.length,
    };
  }, [messages, limit, extra, streamingId, retryingKey]);

  const showMore = useCallback(() => {
    if (limit === 0) return;
    setState(prev => {
      const max = Math.max(0, prev.length - limit);
      const next = Math.min(prev.extra + step, max);
      return next === prev.extra ? prev : { ...prev, extra: next };
    });
  }, [limit, step]);

  return { visible, hiddenCount, showMore };
}
